import $ from 'licia/$';
import h from 'licia/h';
import escape from 'licia/escape';
import uniqId from 'licia/uniqId';
import isObj from 'licia/isObj';
import defaults from 'licia/defaults';
import map from 'licia/map';
import toNum from 'licia/toNum';
import toStr from 'licia/toStr';
import isFn from 'licia/isFn';
import last from 'licia/last';
import isRegExp from 'licia/isRegExp';
import isStr from 'licia/isStr';
import trim from 'licia/trim';
import contain from 'licia/contain';
import lowerCase from 'licia/lowerCase';
import isNull from 'licia/isNull';
import each from 'licia/each';
import { micromark } from 'micromark';
import Component from '../share/Component';
import { exportCjs } from '../share/util';
import { progress } from './util';
export default class Setting extends Component {
    constructor(container, options = {}) {
        super(container, { compName: 'setting' }, options);
        this.items = [];
        this.selectedItem = null;
        this.initOptions(options, {
            separatorCollapse: true,
            filter: '',
        });
        this.bindEvent();
    }
    appendTitle(title, level = 1) {
        const settingTitle = new LunaSettingTitle(this, title, level);
        this.append(settingTitle);
        return settingTitle;
    }
    appendSeparator() {
        const { items } = this;
        const { separatorCollapse } = this.options;
        const lastItem = last(items);
        if (separatorCollapse && lastItem instanceof LunaSettingSeparator) {
            return lastItem;
        }
        const settingSeparator = new LunaSettingSeparator(this);
        this.append(settingSeparator);
        return settingSeparator;
    }
    appendNumber(key, value, title, description, options) {
        if (isObj(description)) {
            options = description;
            description = '';
        }
        const settingNumber = new LunaSettingNumber(this, key, value, title, description, options);
        this.append(settingNumber);
        return settingNumber;
    }
    appendButton(title, description, handler) {
        if (isFn(description)) {
            handler = description;
            description = '';
        }
        const settingButton = new LunaSettingButton(this, title, description, handler);
        this.append(settingButton);
        return settingButton;
    }
    appendHtml(html) {
        const settingHtml = new LunaSettingHtml(this, html);
        this.append(settingHtml);
        return settingHtml;
    }
    appendMarkdown(markdown) {
        const settingMarkdown = new LunaSettingMarkdown(this, markdown);
        this.append(settingMarkdown);
        return settingMarkdown;
    }
    appendInput(key, value, title, description = '') {
        const settingInput = new LunaSettingInput(this, key, value, title, description);
        this.append(settingInput);
        return settingInput;
    }
    appendCheckbox(key, value, title, description) {
        if (!description) {
            description = title;
            title = '';
        }
        const settingCheckbox = new LunaSettingCheckbox(this, key, value, title, description);
        this.append(settingCheckbox);
        return settingCheckbox;
    }
    appendSelect(key, value, title, description, options) {
        if (isObj(description)) {
            options = description;
            description = '';
        }
        const settingSelect = new LunaSettingSelect(this, key, value, title, description, options);
        this.append(settingSelect);
        return settingSelect;
    }
    remove(item) {
        const { items } = this;
        const pos = items.indexOf(item);
        if (pos > -1) {
            item.detach();
            items.splice(pos, 1);
            if (item === this.selectedItem) {
                this.selectItem(null);
            }
        }
    }
    clear() {
        each(this.items, (item) => item.detach());
        this.items = [];
        this.selectItem(null);
    }
    selectItem(item) {
        if (this.selectedItem) {
            this.selectedItem.deselect();
            this.selectedItem = null;
        }
        if (!isNull(item)) {
            this.selectedItem = item;
            this.selectedItem?.select();
        }
    }
    renderSettings() {
        const { items } = this;
        each(items, (item) => item.detach());
        each(items, (item) => {
            if (this.filterItem(item)) {
                this.$container.append(item.container);
            }
        });
    }
    bindEvent() {
        const { c } = this;
        this.on('changeOption', (name) => {
            switch (name) {
                case 'filter':
                    this.renderSettings();
                    break;
            }
        });
        const self = this;
        this.$container.on('click', c('.item'), function () {
            self.selectItem(this.settingItem);
        });
    }
    filterItem(item) {
        let { filter } = this.options;
        if (filter) {
            if (isFn(filter)) {
                return filter(item);
            }
            else if (isRegExp(filter)) {
                return filter.test(item.text());
            }
            else if (isStr(filter)) {
                filter = trim(filter);
                if (filter) {
                    return contain(lowerCase(item.text()), lowerCase(filter));
                }
            }
        }
        return true;
    }
    append(item) {
        this.items.push(item);
        if (this.filterItem(item)) {
            this.$container.append(item.container);
        }
    }
}
export class LunaSettingItem {
    constructor(setting, key, value, type) {
        this.container = h('div', {
            tabindex: '0',
        });
        this.setting = setting;
        this.container.settingItem = this;
        this.$container = $(this.container);
        this.$container
            .addClass(setting.c('item'))
            .addClass(setting.c(`item-${type}`));
        this.key = key;
        this.value = value;
    }
    select() {
        this.$container.addClass(this.setting.c('selected'));
    }
    deselect() {
        this.$container.rmClass(this.setting.c('selected'));
    }
    detach() {
        this.$container.remove();
    }
    disable() {
        this.$container.addClass(this.setting.c('disabled'));
    }
    enable() {
        this.$container.rmClass(this.setting.c('disabled'));
    }
    text() {
        return this.$container.text();
    }
    onChange(value) {
        if (this.value !== value) {
            this.setting.emit('change', this.key, value, this.value);
            this.value = value;
        }
    }
}
export class LunaSettingTitle extends LunaSettingItem {
    constructor(setting, title, level) {
        super(setting, '', '', 'title');
        this.$container.addClass(setting.c(`level-${level}`));
        this.$container.text(title);
    }
}
export class LunaSettingMarkdown extends LunaSettingItem {
    constructor(setting, markdown) {
        super(setting, '', '', 'markdown');
        this.$container.html(micromark(markdown));
    }
}
export class LunaSettingSeparator extends LunaSettingItem {
    constructor(setting) {
        super(setting, '', '', 'separator');
    }
}
export class LunaSettingInput extends LunaSettingItem {
    constructor(setting, key, value, title, description) {
        super(setting, key, value, 'input');
        const { c } = setting;
        this.$container.html(`<div class="${c('title')}">${escape(title)}</div>
      <div class="${c('description')}">${micromark(description)}</div>
      <div class="${c('control')}">
        <input type="text"></input>
      </div>`);
        const $input = this.$container.find('input');
        $input.val(value);
        $input.on('change', () => this.onChange($input.val()));
        this.$input = $input;
    }
    setValue(value) {
        this.$input.val(value);
        this.value = value;
    }
    disable() {
        super.disable();
        this.$input.attr('disabled', '');
    }
    enable() {
        super.enable();
        this.$input.rmAttr('disabled');
    }
}
export class LunaSettingNumber extends LunaSettingItem {
    constructor(setting, key, value, title, description, options = {}) {
        super(setting, key, value, 'number');
        this.renderRange = () => {
            const value = toNum(this.$input.val());
            const { options } = this;
            this.$trackProgress.css('width', progress(value, options.min, options.max) + '%');
            this.$value.text(toStr(value));
        };
        defaults(options, {
            min: 0,
            max: 10,
            step: 1,
        });
        this.options = options;
        const { $container } = this;
        const { c } = setting;
        const range = !!options.range;
        delete options.range;
        const min = options.min;
        const max = options.max;
        let input = `<input type="${range ? 'range' : 'number'}"${map(options, (val, key) => ` ${key}="${val}"`)}></input>`;
        if (range) {
            input = `${min}<div class="${c('range-container')}">
        <div class="${c('range-track')}">
          <div class="${c('range-track-bar')}">
            <div class="${c('range-track-progress')}" style="width: ${progress(value, min, max)}%;"></div>
          </div>
        </div>
        ${input}
      </div><span class="${c('value')}">${value}</span>/${max}`;
        }
        $container.html(`<div class="${c('title')}">${escape(title)}</div>
      <div class="${c('description')}">${micromark(description)}</div>
      <div class="${c('control')}">${input}</div>`);
        const $input = $container.find('input');
        this.$value = $container.find(c('.value'));
        this.$trackProgress = $container.find(c('.range-track-progress'));
        $input.val(toStr(value));
        $input.on('change', () => {
            const val = toNum($input.val());
            this.onChange(val);
        });
        $input.on('input', this.renderRange);
        this.$input = $input;
    }
    setValue(value) {
        this.$input.val(toStr(value));
        this.value = value;
        this.renderRange();
    }
    disable() {
        super.disable();
        this.$input.attr('disabled', '');
    }
    enable() {
        super.enable();
        this.$input.rmAttr('disabled');
    }
}
export class LunaSettingCheckbox extends LunaSettingItem {
    constructor(setting, key, value, title, description) {
        super(setting, key, value, 'checkbox');
        const { c } = setting;
        const id = uniqId(setting.c('checkbox-'));
        this.$container.html(`<div class="${c('title')}">${escape(title)}</div>
      <div class="${c('control')}">
        <input type="checkbox" id="${id}"></input>
        <label for="${id}">${micromark(description)}</label>
      </div>`);
        const $input = this.$container.find('input');
        const input = $input.get(0);
        input.checked = value;
        $input.on('change', () => this.onChange(input.checked));
        this.$input = $input;
        this.input = input;
    }
    setValue(value) {
        this.input.checked = value;
        this.value = value;
    }
    disable() {
        super.disable();
        this.$input.attr('disabled', '');
    }
    enable() {
        super.enable();
        this.$input.rmAttr('disabled');
    }
}
export class LunaSettingSelect extends LunaSettingItem {
    constructor(setting, key, value, title, description, options) {
        super(setting, key, value, 'select');
        const { c } = setting;
        this.$container.html(`<div class="${c('title')}">${escape(title)}</div>
      <div class="${c('description')}">${micromark(description)}</div>
      <div class="${c('control')}">
        <div class="${c('select')}">
          <select></select>
        </div>
      </div>`);
        const $select = this.$container.find('select');
        this.$select = $select;
        this.setOptions(options);
        $select.on('change', () => this.onChange($select.val()));
    }
    setValue(value) {
        this.$select.val(value);
        this.value = value;
    }
    setOptions(options) {
        this.$select.html(map(options, (val, key) => `<option value="${escape(val)}"${val === this.value ? ' selected' : ''}>${escape(key)}</option>`).join(''));
    }
    disable() {
        super.disable();
        this.$select.attr('disabled', '');
    }
    enable() {
        super.enable();
        this.$select.rmAttr('disabled');
    }
}
export class LunaSettingButton extends LunaSettingItem {
    constructor(setting, title, description, handler) {
        super(setting, '', '', 'button');
        if (!description) {
            description = title;
            title = '';
        }
        this.$container.html(setting.c(`<div class="title">${escape(title)}</div>
      <div class="control">
        <button>${escape(description)}</button>
      </div>`));
        const $button = this.$container.find('button');
        $button.on('click', handler);
    }
}
export class LunaSettingHtml extends LunaSettingItem {
    constructor(setting, html) {
        super(setting, '', '', 'html');
        this.$container.append(html);
    }
}
if (typeof module !== 'undefined') {
    exportCjs(module, Setting);
}
